<template>
  <div>
    <div class="md:pt-6 mt-4">
      <div v-if="notInApplication">
        <router-link to="/uploadHub" class="font-bold text-blue-500 hover:text-blue-800"
          ><i class="fas fa-arrow-left mr-2 font-bold text-blue-500 hover:text-blue-800"></i>Back to
          upload hub</router-link
        >
      </div>
      <h1 class="text-xl font-semibold pb-2">{{ title }}</h1>
      <h3 class="pb-3 border-b mb-4">{{ description }}</h3>
    </div>

    <div class="flex md:flex-row flex-col w-full" :class="'card-shadow dashboard-card'">
      <div v-for="index in fileCountArray" v-bind:key="index" class="p-2 w-full">
        <vue-dropzone
          class="z-0 md:rounded bg-red bg-white"
          :class="'dropzone-upload-page'"
          v-on:vdropzone-file-added="(file) => afterComplete(file, index)"
          v-on:vdropzone-removed-file="(file) => afterRemoved(file, index)"
          v-on:vdropzone-max-files-exceeded="(file) => maxFilesExceeded(file, index)"
          :useCustomSlot="true"
          ref="myVueDropzone"
          id="dropzone"
          :options="dropzoneOptions"
        >
          <div class="dropzone-custom-content">
            <h3 class="dropzone-custom-title font-black text-gray-700">
              Drag and drop to upload content!
            </h3>
            <div class="subtitle text-gray-500">
              ...or click to select a file from your computer
            </div>
            <div><i class="file-upload-icon fas fa-cloud-upload-alt"></i></div>
          </div>
        </vue-dropzone>
      </div>
    </div>
    <div class="mt-2">
      <transition name="in--up">
        <div v-if="globalErrorMessage" class="flex items-center justify-center w-full px-4">
          <h3 class="text-red-500 text-sm">{{ globalErrorMessage }}</h3>
        </div>
      </transition>
    </div>
    <div class="flex w-full mt-3 px-5">
      <button
        v-if="backButton"
        class="hover:text-primary mr-auto focus:outline-none rounded"
        type="btn"
        aria-label="navigate back"
        @click="$emit('back')"
      >
        <i
          class="fas fa-arrow-left bg-gray-300 rounded-full flex items-center text-lg justify-center w-8 h-8"
        ></i>
      </button>

      <base-button
        type="primary ml-auto"
        :disabled="!nextEnabled"
        aria-label="navigate next"
        class="mb-4"
        @click="upload()"
      >
        {{ uploadLabel }}
      </base-button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import gql from 'graphql-tag'
import Swal from 'sweetalert2'
import VueToast from 'vue-toast-notification'
// TODO: check if this CSS should go here or in CSS directory
import 'vue-toast-notification/dist/theme-default.css'
// import 'vue-toast-notification/dist/theme-sugar.css';
Vue.use(VueToast)

const axios = require('axios')

export default {
  name: 'FileDrop',
  props: {
    categoryProp: {
      default: 'route',
      type: String
    },
    backButton: {
      default: false,
      type: Boolean
    },
    altDescription: String,
    maxFileSize: Number
  },
  components: {
    vueDropzone: vue2Dropzone
  },
  data: function () {
    return {
      retryCounter: 0,
      retryTimer: 5,
      dropzoneOptions: {
        url: 'https://httpbin.org/post',
        thumbnailWidth: 200,
        addRemoveLinks: true,
        maxFiles: 1
      },
      uploadFiles: [],
      acceptableFileTypes: [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg',
        'video/quicktime',
        'video/mp4'
      ],
      loading: false,
      globalErrorMessage: ''
    }
  },
  computed: {
    notInApplication() {
      return !window.location.href.includes('apply')
    },
    fileCountArray() {
      return [...Array(this.fileCount).keys()]
    },
    uploadLabel() {
      return this.fileCount > 1 ? 'Upload Files' : 'Upload File'
    },
    category() {
      if (this.categoryProp != 'route') return this.categoryProp
      return !this.$route.query.category ? 'other' : this.$route.query.category
    },
    title() {
      switch (this.category) {
        case 'other':
          return 'File Upload'
        case 'tenure':
          return 'Start Date Verification'
        case 'income':
          return 'Income Verification'
        case 'paystubs':
          return 'Income Verification'
        case 'additionalPaystubs':
          return 'Income Verification'
        case 'identity':
          return 'Identity Verification'
      }
      return ''
    },
    description() {
      switch (this.category) {
        case 'other':
          return 'Please drag and drop a file into the area below.'
        case 'tenure':
          return !this.altDescription
            ? 'Please upload a document verifying your start date.  A screen shot including your hire date from your employee self-service portal is acceptable.'
            : this.altDescription
        case 'income':
          return !this.altDescription
            ? 'In order to verify your income, please upload a copy of your two most recent pay-stubs. Please make sure the files are in PDF file format.'
            : this.altDescription
        case 'paystubs':
          return !this.altDescription
            ? 'In order to verify your income, please upload a copy of your two most recent pay-stubs. Please make sure the files are in PDF file format.'
            : this.altDescription
        case 'additionalPaystubs':
          return !this.altDescription
            ? 'In order to verify your income, please upload a copy of your pay-stubs. Please make sure the files are in PDF file format.'
            : this.altDescription
        case 'identity':
          return !this.altDescription
            ? 'Please upload a color copy of a government issued photo ID with all four corners visible. If you take this picture with your phone, please lay the ID on a plain background (like a sheet of paper) and take the picture from directly above with the flash turned off.'
            : this.altDescription
      }
      return ''
    },
    fileCount() {
      return this.category === 'income' || this.category === 'paystubs' ? 2 : 1
    },
    nextEnabled() {
      return !this.loading && this.uploadFiles.filter((f) => f.urls).length >= this.fileCount
    },
    fileUrls() {
      return this.uploadFiles
    },
    supportedFileTypes() {
      if (this.category === 'identity') {
        return ['image/png', 'image/jpeg', 'image/jpg']
      } else if (['income', 'paystubs', 'additionalPaystubs'].includes(this.category)) {
        return this.pdfPaystubs
          ? ['application/pdf']
          : ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']
      }
      return this.acceptableFileTypes
    },
    pdfPaystubs() {
      return this.$store.getters.pdfPaystubs
    }
  },
  watch: {
    retryCounter() {
      this.upload()
    }
  },
  methods: {
    unsupportedFileErrorMessage() {
      let error = `Please only upload files with the extension .jpg, .jpeg, .png`
      if (this.category !== 'identity') {
        error += ', or pdf'
      }
      if (
        ['income', 'paystubs', 'additionalPaystubs'].includes(this.category) &&
        this.pdfPaystubs
      ) {
        error = `Please only upload files with the extension .pdf`
      }
      return error
    },
    maxSizeErrorMessage() {
      const bytesToMb = (this.maxFileSize / 1024) * 1024

      const error = `Please only upload files less than ${bytesToMb} MB`

      return error
    },
    async afterComplete(file, index) {
      this.globalErrorMessage = ''
      const incomeFileTypes = this.supportedFileTypes
      const fileUploadType = file.type
      if (!incomeFileTypes.includes(fileUploadType)) {
        console.log('invalid type')
        this.$refs.myVueDropzone[index].removeFile(file)
        this.globalErrorMessage = this.unsupportedFileErrorMessage()

        return
      }

      if (this.maxFileSize && file.size > this.maxFileSize) {
        console.log('invalid size')
        this.$refs.myVueDropzone[index].removeFile(file)
        this.globalErrorMessage = this.maxSizeErrorMessage()
        return
      }

      const { data } = await this.$apiClient
        .mutate({
          mutation: gql(
            `mutation { uploads(filename: "${file.name}", category: "${
              this.category === 'other' ? 'dashboard_upload' : this.category
            }") }`
          )
        })
        .catch((e) => {
          this.globalErrorMessage = e
        })

      if (!this.uploadFiles[index] || !this.uploadFiles[index].file) {
        this.$set(this.uploadFiles, index, { file, urls: data.uploads })
      }
    },
    afterRemoved(file, index) {
      if (this.uploadFiles[index] && this.uploadFiles[index].file.name === file.name) {
        this.$set(this.uploadFiles, index, {})
      }
    },
    clearAllFiles() {
      this.uploadFiles = []
      this.$refs.myVueDropzone.forEach((file) => {
        file.removeAllFiles()
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    maxFilesExceeded(file, index) {
      this.$refs.myVueDropzone[index].removeFile(file)
    },
    upload() {
      this.loading = true
      const uploadFileNames = this.uploadFiles.map(({ file }) => file.name)
      const uploadFileTypes = this.uploadFiles.map(({ file }) => file.type)
      const uniqueUploadFileNames = new Set(uploadFileNames)
      const invalidFileTypes = uploadFileTypes.filter((ft) => !this.supportedFileTypes.includes(ft))
      console.log(('invalid ', invalidFileTypes))
      if (invalidFileTypes.length > 0) {
        this.globalErrorMessage = this.unsupportedFileErrorMessage()
        this.clearAllFiles()
        this.loading = false
      } else if (uniqueUploadFileNames.size !== uploadFileNames.length) {
        this.globalErrorMessage = 'Please make sure all file names are unique.'
        this.clearAllFiles()
        this.loading = false
      } else {
        return this.uploadFilesAxios()
          .then(() => {
            this.loading = false
            let self = this
            // Todo: make sure this is only for the upload page, and not for the app process.
            // if not on the apply page
            if (!window.location.href.includes('apply')) {
              Swal.fire({
                title: 'File Uploaded Successfully.',
                confirmButtonColor: '#69D393',
                text: 'You can upload more files or go back to the home page.',
                icon: 'info',
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: `Upload more files`,
                cancelButtonText: `Go to home page`
              }).then(function (result) {
                if (result.value) {
                  // Clicked upload more files
                  self.clearAllFiles()
                } else {
                  // Clicked go to home
                  self.$router.push({ name: 'Dashboard' })
                }
              })
            }
            this.clearAllFiles()
            Vue.prototype.$apiClient.mutate({
              mutation: gql(`mutation {
                logs(message: "${encodeURI(`${this.category} document uploaded.`)}")
              }`)
            })
            this.$emit('next')
          })
          .catch((e) => {
            Vue.$toast.clear()
            this.loading = true
            if (this.retryCounter < 1) {
              Vue.$toast.open({
                message:
                  'Some files failed to upload. Please wait while we attempt to re-upload the files.', // The message to show
                type: 'info', // Message type: [success, info, warning, error, default]
                duration: 5000, // duration in ms, 0 for sticky toast
                position: 'top-right'
              })
            }
            let self = this
            setTimeout(function () {
              if (self.retryCounter < 1) {
                self.retryCounter += 1
              }
            }, 7000)

            if (self.retryCounter >= 1) {
              Swal.fire({
                title: 'Failed to upload files.',
                confirmButtonColor: '#69D393',
                text: `There appears to be a network error when attempting to upload this document. You should receive an email with instructions on how to provide the file over email. ${
                  window.location.href.includes('apply')
                    ? 'You will be able to move to the next step, but your application is incomplete until we receive the required files.'
                    : ''
                }`,
                icon: 'info',
                showCloseButton: true,
                confirmButtonText: `Ok`
              }).then(function () {
                self.requestDocuments().then(() => {
                  self.loading = false
                  self.clearAllFiles()
                  self.$emit('next')
                })
              })
            }
            console.error(e)
          })
      }
    },
    async uploadFilesAxios() {
      let promises = []
      this.uploadFiles.map(({ file, urls }) => {
        for (const url of urls) {
          promises.push(
            axios.put(url, file, {
              headers: {
                'Content-Type': file.type
              }
            })
          )
        }
      })
      return Promise.all(promises)
        .then(() => {
          console.log('all done')
        })
        .catch((e) => {
          console.log('error happened: ', e)
          console.log(`FE Error in uploadFilesAxios: ${e}`)
          throw e
        })
    },
    next() {
      this.$emit('next')
    },
    requestDocuments() {
      const category =
        this.category === 'paystubs' || this.category === 'additionalPaystubs'
          ? 'income'
          : this.category

      if (category === 'other') return

      const username = this.$store.getters.username
      const lastName = this.$store.getters.lastName

      const mutationStr = `mutation { documentRequest(username: "${username}", category: "${category}", context: "No Context", lastName: "${lastName}") }`

      this.$apiClient
        .mutate({
          mutation: gql(mutationStr)
        })
        .then(({ data }) => {
          if (data.documentRequest !== 200) throw data.documentRequest
        })
        .catch((e) => {
          console.log(e)
        })
    }
  }
}
</script>
<style lang="scss">
.base-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  i {
    padding: 0 3px;
  }
}
.dropzone-custom-content {
  position: relative;
  text-align: center;
  z-index: 10;
}
.dz-message {
  display: inline-block;
  display: flex;
  justify-content: center;
  align-items: center;
}
.dropzone {
  min-height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.dropzone-custom-title {
  margin-top: 0;
}
.file-upload-icon {
  font-size: 6em !important;
  opacity: 0.15;
  position: absolute;
  left: 29%;
  top: -20%;
}
</style>
